<template>
  <div class="overview-container">
    <!-- Pie Chart Section -->
    <div class="chart-section">
      <h3 class="section-title">{{ moment().format('MMMM') }} Sales Distribution</h3>
      <div class="chart-wrapper">
        <pie-chart :data="pie"></pie-chart>
      </div>
    </div>

    <!-- Year Selector Tabs -->
    <div class="year-tabs">
      <button
        :class="['year-tab', selectedYear === 'current' ? 'active' : '']"
        @click="selectedYear = 'current'">
        {{ curYear }}
      </button>
      <button
        :class="['year-tab', selectedYear === 'previous' ? 'active' : '']"
        @click="selectedYear = 'previous'">
        {{ prevYear }}
      </button>
    </div>

    <!-- Current Year Data -->
    <div v-show="selectedYear === 'current'" class="data-section">
      <!-- Mobile View: Accordion -->
      <div class="d-md-none mobile-accordion">
        <div
          v-for="(t, i) of rawDataCur"
          :key="'mobile-cur-' + t.name"
          class="accordion-item">
          <div
            class="accordion-header"
            :class="[getHeaderClass(t.name, i), expandedCategories.includes(`cur-${i}`) ? 'expanded' : '']"
            @click="toggleCategory(`cur-${i}`)">
            <div class="header-content">
              <span class="category-name">{{ t.name.toUpperCase() }}</span>
              <span class="category-total">
                {{ getCategoryTotal(t.data) }} NOK
              </span>
            </div>
            <b-icon
              :icon="expandedCategories.includes(`cur-${i}`) ? 'chevron-up' : 'chevron-down'"
              class="expand-icon"></b-icon>
          </div>
          <div v-show="expandedCategories.includes(`cur-${i}`)" class="accordion-body">
            <div
              v-for="line of t.data"
              :key="line.month"
              class="month-row">
              <span class="month-label">{{ monthMap[line.month] }}</span>
              <span class="month-value">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop View: Tables -->
      <div class="d-none d-md-block desktop-tables">
        <div class="tables-grid">
          <div
            v-for="(t, i) of rawDataCur"
            :key="'desktop-cur-' + t.name"
            class="table-card">
            <div class="table-header" :class="getHeaderClass(t.name, i)">
              {{ t.name.toUpperCase() }}
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="text-right">NOK</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line of t.data" :key="line.month">
                  <td>{{ monthMap[line.month] }}</td>
                  <td class="text-right">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Previous Year Data -->
    <div v-show="selectedYear === 'previous'" class="data-section">
      <!-- Mobile View: Accordion -->
      <div class="d-md-none mobile-accordion">
        <div
          v-for="(t, i) of rawDataPrev"
          :key="'mobile-prev-' + t.name"
          class="accordion-item">
          <div
            class="accordion-header"
            :class="[getHeaderClass(t.name, i), expandedCategories.includes(`prev-${i}`) ? 'expanded' : '']"
            @click="toggleCategory(`prev-${i}`)">
            <div class="header-content">
              <span class="category-name">{{ t.name.toUpperCase() }}</span>
              <span class="category-total">
                {{ getCategoryTotal(t.data) }} NOK
              </span>
            </div>
            <b-icon
              :icon="expandedCategories.includes(`prev-${i}`) ? 'chevron-up' : 'chevron-down'"
              class="expand-icon"></b-icon>
          </div>
          <div v-show="expandedCategories.includes(`prev-${i}`)" class="accordion-body">
            <div
              v-for="line of t.data"
              :key="line.month"
              class="month-row">
              <span class="month-label">{{ monthMap[line.month] }}</span>
              <span class="month-value">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop View: Tables -->
      <div class="d-none d-md-block desktop-tables">
        <div class="tables-grid">
          <div
            v-for="(t, i) of rawDataPrev"
            :key="'desktop-prev-' + t.name"
            class="table-card">
            <div class="table-header" :class="getHeaderClass(t.name, i)">
              {{ t.name.toUpperCase() }}
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="text-right">NOK</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line of t.data" :key="line.month">
                  <td>{{ monthMap[line.month] }}</td>
                  <td class="text-right">{{ line.totalSales.toLocaleString('en', { maximumFractionDigits: 0 }) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios';
import fixed from '@/fixed';
import moment from 'moment';

export default {
  data: () => ({
    curYear: moment().format('YYYY'),
    prevYear: moment().subtract(1, 'years').format('YYYY'),
    chart: [],
    pie: [],
    rawDataCur: [],
    rawDataPrev: [],
    selectedYear: 'current', // For tab switching
    expandedCategories: [], // For mobile accordion
    monthMap: {
      1: 'Jan',
      2: 'Feb',
      3: 'Mar',
      4: 'Apr',
      5: 'May',
      6: 'Jun',
      7: 'Jul',
      8: 'Aug',
      9: 'Sep',
      10: 'Okt',
      11: 'Nov',
      12: 'Dec',
    },
    moment,
  }),
  async mounted() {
    const rwc = await this.getOverview(this.curYear);
    const rwp = await this.getOverview(this.prevYear);

    this.rawDataCur = rwc.sort((a, b) => a.name.localeCompare(b.name));
    this.rawDataPrev = rwp.sort((a, b) => a.name.localeCompare(b.name));

    this.decorateTotals(this.rawDataCur);
    this.decorateTotals(this.rawDataPrev);

    this.pie = this.generatePieFormat(this.rawDataCur);
    // this.chart = this.generateChartFormat(this.rawDataPrev);
  },
  methods: {
    toggleCategory(categoryId) {
      const index = this.expandedCategories.indexOf(categoryId);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryId);
      }
    },
    getCategoryTotal(data) {
      const total = data.reduce((sum, line) => sum + line.totalSales, 0);
      return total.toLocaleString('en', { maximumFractionDigits: 0 });
    },
    getHeaderClass(name) {
      // Check if it's the "Total" category
      if (name.toLowerCase() === 'total') {
        return 'header-total';
      }
      return 'header-category';
    },
    decorateTotals(dataset) {
      // Calculate total sales per month across all names
      const totalSalesPerMonth = dataset.reduce((acc, { data }) => {
        data.forEach(({ month, totalSales }) => {
          acc[month] = (acc[month] || 0) + totalSales;
        });
        return acc;
      }, {});

      // Convert the aggregated totals into the desired format
      const totalData = Object.entries(totalSalesPerMonth).map(([month, totalSales]) => ({
        month: parseInt(month, 10),
        totalSales,
      }));

      // Sort by month
      totalData.sort((a, b) => a.month - b.month);

      // Add the total entry to the dataset
      dataset.push({ name: 'Total', data: totalData });
    },
    generateChartFormat(data) {
      const chartData = [];
      data.forEach((source) => {
        const record = {
          name: source.name,
          data: source.data.map((line) => [[this.monthMap[line.month]], line.totalSales]),
        };
        chartData.push(record);
      });
      return chartData;
    },
    generatePieFormat(data) {
      const pieData = [];
      for (let i = 0; i < data.length - 1; i += 1) {
        pieData.push([data[i].name, data[i].data[data[i].data.length - 1].totalSales]);
      }
      return pieData;
    },
    async getOverview(year) {
      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/admin/sales-overview?year=${year}`,
          { withCredentials: true },
        );
        return data;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.$toast.warning('you\'re not authorised for this action ✋🏻');
        } else {
          this.$toast.info('No data for selected range');
        }
        return null;
      }
    },
  },
};
</script>

<style scoped>
/* Mobile-first Container */
.overview-container {
  padding: 1rem;
  min-height: 100vh;
  background-color: #222222;
}

/* Chart Section */
.chart-section {
  background-color: #343a40;
  border-radius: 0.5rem;
  padding: 1.5rem 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.section-title {
  font-size: 1.25rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
  color: #fff;
  text-transform: capitalize;
}

.chart-wrapper {
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1rem;
}

/* Year Tabs */
.year-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.year-tab {
  flex: 1;
  padding: 0.875rem 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  border: 2px solid #495057;
  background-color: #343a40;
  color: #adb5bd;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.year-tab:hover {
  background-color: #495057;
  color: #fff;
}

.year-tab.active {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

/* Data Section */
.data-section {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile Accordion */
.mobile-accordion {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.accordion-item {
  background-color: #343a40;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  border: 1px solid #495057;
}

.accordion-header {
  padding: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease-in-out;
  user-select: none;
}

.accordion-header:active {
  transform: scale(0.98);
}

.header-category {
  background-color: #495057;
}

.header-total {
  background-color: #28a745;
}

.accordion-header.expanded {
  border-bottom: 1px solid #495057;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.category-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}

.category-total {
  font-size: 0.875rem;
  color: #adb5bd;
  font-weight: 500;
}

/* White text for total category on mobile */
.header-total .category-total {
  color: #fff;
}

.expand-icon {
  font-size: 1.25rem;
  color: #fff;
  transition: transform 0.2s ease-in-out;
}

.accordion-header.expanded .expand-icon {
  transform: rotate(180deg);
}

.accordion-body {
  background-color: #212529;
  animation: slideDown 0.2s ease-in-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

.month-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #343a40;
}

.month-row:last-child {
  border-bottom: none;
}

.month-label {
  font-weight: 600;
  color: #adb5bd;
  font-size: 0.875rem;
}

.month-value {
  font-weight: 700;
  color: #fff;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
}

/* Desktop Tables */
.desktop-tables {
  margin-top: 1rem;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.table-card {
  background-color: #343a40;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 1px solid #495057;
}

.table-header {
  padding: 1rem;
  font-weight: 700;
  font-size: 0.875rem;
  color: #fff;
  text-align: center;
  letter-spacing: 0.5px;
}

.table-header.header-category {
  background-color: #dc3545;
}

.table-header.header-total {
  background-color: #28a745;
}

.stats-table {
  width: 100%;
  margin: 0;
  color: #fff;
  background-color: #212529;
}

.stats-table thead {
  background-color: #495057;
}

.stats-table th {
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  border: none;
  color: #fff;
}

.stats-table tbody tr {
  border-bottom: 1px solid #343a40;
  transition: background-color 0.15s ease-in-out;
}

.stats-table tbody tr:last-child {
  border-bottom: none;
}

.stats-table tbody tr:hover {
  background-color: #343a40;
}

.stats-table td {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  border: none;
  color: #e9ecef;
}

.stats-table .text-right {
  text-align: right;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

/* Tablet Responsive (sm breakpoint) */
@media (min-width: 576px) {
  .overview-container {
    padding: 1.5rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .chart-section {
    padding: 2rem;
  }

  .year-tab {
    font-size: 1.25rem;
    padding: 1rem 1.5rem;
  }

  .accordion-header {
    padding: 1.25rem 1.5rem;
  }

  .category-name {
    font-size: 1.125rem;
  }

  .category-total {
    font-size: 1rem;
  }

  .month-row {
    padding: 1rem 1.5rem;
  }

  .month-label,
  .month-value {
    font-size: 1rem;
  }
}

/* Desktop Responsive (md breakpoint) */
@media (min-width: 768px) {
  .overview-container {
    padding: 2rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}

/* Large Desktop (lg breakpoint) */
@media (min-width: 992px) {
  .overview-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
  }
}

/* Extra Large Desktop (xl breakpoint) */
@media (min-width: 1200px) {
  .tables-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
